import { CalendarOutlined, ReloadOutlined } from "@ant-design/icons";
import { Col, Flex, Typography } from "antd";
import { useMemo } from "react";
import { useAppSelector } from "../../hooks/redux-hooks";
import { cooldownLabel } from "../EhrSyncButton/copy";
import styles from "./createnotemodal.module.scss";
import { buildSchedulePickBar } from "./schedulePick.utils";
import { useSchedulePickContext } from "./useSchedulePick";

const TONE: Record<"indigo" | "neutral" | "warn", { bg: string; border: string; text: string; link: string }> = {
  indigo: {
    bg: "var(--indigo-50)",
    border: "var(--secondary-blue-bg)",
    text: "var(--indigo-900)",
    link: "var(--indigo-700)",
  },
  neutral: { bg: "var(--grey-50)", border: "var(--grey-200)", text: "var(--grey-600)", link: "var(--primary)" },
  warn: { bg: "#fffbeb", border: "#fde68a", text: "#78350f", link: "#b45309" },
};

/**
 * The one-line bar under the patient field: what the drawer picked and why, with
 * the escape hatch beside it. Copy comes from ``buildSchedulePickBar`` so every
 * state is data; this component only paints it. Renders nothing for accounts
 * without EHR sync, so their drawer is unchanged.
 */
const SchedulePickBar = () => {
  const componentName = "createnotemodal";
  const ctx = useSchedulePickContext();
  const { recordingDetail } = useAppSelector((state) => state.recordings);

  const model = useMemo(
    () =>
      ctx.enabled
        ? buildSchedulePickBar({
            pick: ctx.pick,
            autopick: ctx.autopick,
            selectedClientId: recordingDetail?.client_id,
            lastSource: ctx.lastSource,
            zone: ctx.clinicZone,
          })
        : null,
    [ctx.enabled, ctx.pick, ctx.autopick, ctx.lastSource, ctx.clinicZone, recordingDetail?.client_id],
  );

  if (!model) return null;
  const tone = TONE[model.tone];
  const syncing = ctx.refreshPhase === "running";
  const coolingDown = ctx.refreshPhase === "cooldown";

  const onAction = () => {
    if (!model.action) return;
    if (model.action.kind === "switch") ctx.requestOpenPicker();
    else if (model.action.kind === "use_pick") ctx.applySuggestion();
    else if (model.action.kind === "sync" && !syncing && !coolingDown) ctx.refresh();
  };

  const actionLabel = (() => {
    if (!model.action) return null;
    if (model.action.kind !== "sync") return model.action.label;
    if (syncing) return "Syncing…";
    if (coolingDown) return `Synced · try again in ${cooldownLabel(ctx.refreshCooldownS)}`;
    return model.action.label;
  })();

  return (
    <Col span={24} className={styles[`${componentName}__modal-row`]}>
      <Flex
        align="center"
        justify="space-between"
        gap={12}
        role="status"
        aria-live="polite"
        data-testid="schedule-pick-bar"
        data-tone={model.tone}
        style={{
          marginTop: 8,
          padding: "8px 12px",
          borderRadius: 8,
          background: tone.bg,
          border: `1px solid ${tone.border}`,
          color: tone.text,
          fontSize: 13,
        }}
      >
        <Flex align="center" gap={8} style={{ minWidth: 0 }}>
          <CalendarOutlined style={{ color: tone.link, flexShrink: 0 }} />
          <Typography.Text style={{ color: tone.text, fontSize: 13 }}>
            <strong>{model.lead}</strong>
            {model.detail.map((part) => (
              <span key={part}>
                {" · "}
                {part}
              </span>
            ))}
          </Typography.Text>
        </Flex>
        {model.action && actionLabel && (
          <Typography.Link
            onClick={onAction}
            aria-disabled={model.action.kind === "sync" && (syncing || coolingDown)}
            style={{ color: tone.link, fontWeight: 500, whiteSpace: "nowrap", flexShrink: 0, fontSize: 13 }}
          >
            {model.action.kind === "sync" && <ReloadOutlined spin={syncing} style={{ marginRight: 4 }} />}
            {actionLabel}
          </Typography.Link>
        )}
      </Flex>
    </Col>
  );
};

export default SchedulePickBar;
